import { useEffect, useCallback, useState } from 'react'
import { Layout } from '../components/layout'
import { useSearchParams, useNavigate } from 'react-router-dom'
import * as PaddleService from '../utils/PaddleService.js'
import * as GtagService from '../utils/GtagService.js'
import { getUser } from '../firebase'

export default function Checkout() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [failed, setFailed] = useState(false)

  const onMounted = useCallback(async () => {
    const Paddle = await PaddleService.init()

    const planId = searchParams.get('plan_id')
    const teamId = searchParams.get('team_id')
    const discountCode = searchParams.get('discount_code')
    const discountId = searchParams.get('discount_id')
    const additionalMembersId = searchParams.get('additional_members_id')
    const additionalMembersCount = searchParams.get('additional_members_count')
    const transactionId = searchParams.get('_ptxn')

    PaddleService.on('checkout.loaded', (_name, data) => {
      GtagService.beginCheckout({
        value: data.totals.subtotal,
        currency: data.currency_code,
      })
    })

    PaddleService.on('checkout.closed', () => {
      void navigate('/dashboard')
    })

    PaddleService.on('checkout.completed', (_name, data) => {
      GtagService.purchase({
        value: data.totals.subtotal,
        currency: data.currency_code,
        transactionId: data.transaction_id,
      })
      Paddle.Checkout.close()
      const query = new URLSearchParams()
      if (planId) query.set('plan_id', planId)
      if (teamId) query.set('team_id', teamId)
      void navigate(`/checkout-success?${query}`)
    })

    // Open regular checkout
    if (planId && teamId)
      await initCheckout({
        planId,
        teamId,
        discountCode,
        discountId,
        additionalMembersId,
        additionalMembersCount,
      })

    // Load a specific transaction
    if (transactionId) await initTransactionCheckout({ transactionId })
  }, [navigate, searchParams])

  useEffect(() => {
    // Ad blockers block cdn.paddle.com. Without this the page just sits on
    // "Checkout" forever with no checkout and no explanation.
    onMounted().catch((err) => {
      console.error('[Paddle] checkout unavailable:', err)
      setFailed(true)
    })
  }, [onMounted])

  async function initCheckout({
    planId,
    teamId,
    discountCode,
    discountId,
    additionalMembersId,
    additionalMembersCount,
  } = {}) {
    const user = await getUser()

    let items = [
      {
        quantity: 1,
        price_id: planId,
      },
    ]

    if (additionalMembersId) {
      items.push({
        quantity: parseInt(additionalMembersCount),
        price_id: additionalMembersId,
      })
    }

    const checkoutConfig = {
      items,
      customData: {
        uid: user.uid,
        teamId: teamId,
      },
      customer: {
        email: user.email,
      },
    }
    if (discountCode) checkoutConfig.discountCode = discountCode
    else if (discountId) checkoutConfig.discountId = discountId

    // Trigger Paddle checkout
    const Paddle = await PaddleService.get()
    Paddle.Checkout.open(checkoutConfig)
  }

  // Load a specific transaction
  async function initTransactionCheckout({ transactionId } = {}) {
    const Paddle = await PaddleService.get()
    return Paddle.Checkout.open({ transactionId })
  }

  if (failed)
    return (
      <Layout title="Checkout Unavailable">
        <p className="text-gray-500">
          We couldn&apos;t load our payment provider. An ad blocker is the usual
          cause. Disable it and reload, or contact hello@rundownstudio.app.
        </p>
      </Layout>
    )

  return <Layout title="Checkout"></Layout>
}
